/**
 * API Region Selection — eu vs gl
 *
 * Due to client domain whitelisting, we don't know which of
 * `api.eu.cochlear.com` or `api.gl.cochlear.com` a given clinic
 * has whitelisted.  On first load we pick randomly; once a
 * request succeeds we persist that region to localStorage so
 * every subsequent session reuses the working domain.
 *
 * This can also be used in lower environments where both domains
 * are available for testing the failover behavior.
 */

export const API_REGIONS = ['eu', 'gl'];
export const REGION_STORAGE_KEY = 'api_region';

/**
 * Default timeout (ms) for a single region probe request.
 * Short enough to not meaningfully delay the Auth0 redirect,
 * long enough for a high-latency network to respond.
 */
export const PROBE_TIMEOUT_MS = 3000;

/**
 * Resolve which region to use:
 *  1. Return the persisted value from localStorage if present.
 *  2. Otherwise pick randomly between 'eu' and 'gl'.
 *
 * @returns {'eu'|'gl'}
 */
export function getApiRegion() {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(REGION_STORAGE_KEY);
    if (stored && API_REGIONS.includes(stored)) {
      return stored;
    }
  }

  // Random pick — 50/50
  return API_REGIONS[Math.floor(Math.random() * API_REGIONS.length)];
}

/**
 * Persist the successfully used region so all future sessions
 * reuse the same domain.  Call this from your API success handler.
 *
 * @param {'eu'|'gl'} region
 */
export function persistApiRegion(region) {
  if (typeof window !== 'undefined' && API_REGIONS.includes(region)) {
    localStorage.setItem(REGION_STORAGE_KEY, region);
  }
}

/**
 * Clear the persisted region (e.g. for testing or on logout).
 */
export function clearApiRegion() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(REGION_STORAGE_KEY);
  }
}

/**
 * Probe a single region URL for reachability.
 * A GET is used because some API gateways reject HEAD/OPTIONS.
 * Any HTTP response (including 401/403/404) means the domain is
 * reachable — only a network error, CORS failure, or timeout counts
 * as unreachable.
 *
 * @param {string} url - Probe URL for this region
 * @param {number} [timeoutMs=PROBE_TIMEOUT_MS]
 * @returns {Promise<boolean>}
 */
export async function probeRegionUrl(
  url,
  timeoutMs = PROBE_TIMEOUT_MS,
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Probe regions and persist + return the first reachable one.
 *
 * If a region is already stored in localStorage, probe that first.
 * If it is still reachable, return it immediately (fast path).
 * If it is no longer reachable, probe both regions simultaneously
 * and take whichever responds first — ensuring a stale/blocked
 * region is corrected before the Auth0 redirect, preventing a
 * login loop.
 *
 * If both regions fail the function returns null — callers must
 * fall back to getApiRegion() and must not block the redirect.
 *
 * @param {(region: string) => string} buildProbeUrl
 *   Factory that takes 'eu'|'gl' and returns the full probe URL.
 * @param {number} [timeoutMs=PROBE_TIMEOUT_MS]
 * @returns {Promise<string|null>}
 */
export async function probeAndPersistRegion(
  buildProbeUrl,
  timeoutMs = PROBE_TIMEOUT_MS,
) {
  // Fast path: stored region is still reachable — keep it.
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(REGION_STORAGE_KEY);
    if (stored && API_REGIONS.includes(stored)) {
      const reachable = await probeRegionUrl(
        buildProbeUrl(stored),
        timeoutMs,
      );
      if (reachable) return stored;
    }
  }

  // Stored region is stale/blocked (or nothing stored): probe both
  // and take the first reachable one.
  try {
    const winner = await Promise.any(
      API_REGIONS.map(async (region) => {
        const reachable = await probeRegionUrl(
          buildProbeUrl(region),
          timeoutMs,
        );
        if (!reachable) throw new Error(`unreachable:${region}`);
        return region;
      }),
    );

    persistApiRegion(winner);
    return winner;
  } catch {
    // AggregateError — both regions unreachable
    return null;
  }
}
